import { fromOutcomeTaskData, fromOutcomeTaskDataList, toOutcomeTaskUpdate } from '@medprompt/shared';

function asOutcomeTaskList(data) {
  return Array.isArray(data) ? fromOutcomeTaskDataList(data) : [];
}

/**
 * OutcomeTaskService - Handles outcome task API operations
 */
export class OutcomeTaskService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    console.log('OutcomeTaskService initialized');
  }

  /**
   * Fetch all outcome tasks
   * @deprecated Use fetchActiveTasks() instead - this endpoint fetches too much data
   * @returns {Promise<Array>} Outcome tasks
   */
  async fetchOutcomeTasks() {
    const data = await this.apiClient.get('/api/outcome_tasks/fetch');
    return asOutcomeTaskList(data);
  }

  /**
   * Fetch your assigned outcome tasks
   * @returns {Promise<Array>} Your assigned tasks
   */
  async fetchYourTasks(options = {}) {
    try {
      const data = await this.apiClient.get('/api/outcome_tasks/assigned/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchYourTasks received null/undefined data');
        return [];
      }
      
      return asOutcomeTaskList(data);
    } catch (error) {
      console.error('Error fetching your tasks:', error);
      throw error;
    }
  }

  /**
   * Fetch active outcome tasks (for users with DOCS_READ_ALL viewing all tasks)
   * @returns {Promise<Array>} Active tasks
   */
  async fetchActiveTasks(options = {}) {
    try {
      const data = await this.apiClient.get('/api/outcome_tasks/active/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchActiveTasks received null/undefined data');
        return [];
      }
      
      return asOutcomeTaskList(data);
    } catch (error) {
      console.error('Error fetching active tasks:', error);
      throw error;
    }
  }

  /**
   * Fetch archived outcome tasks
   * @returns {Promise<Array>} Archived tasks
   */
  async fetchArchiveTasks(options = {}) {
    try {
      const data = await this.apiClient.get('/api/outcome_tasks/archive/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchArchiveTasks received null/undefined data');
        return [];
      }
      
      return asOutcomeTaskList(data);
    } catch (error) {
      console.error('Error fetching archive tasks:', error);
      throw error;
    }
  }

  /**
   * Update an outcome task
   * @param {Object} taskUpdate - Task update data
   * @returns {Promise<Object>} Updated task
   */
  async updateOutcomeTask(taskUpdate) {
    return this.apiClient.post('/api/outcome_task/update', toOutcomeTaskUpdate(taskUpdate));
  }

  /**
   * Complete an outcome task
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Response data
   */
  async completeOutcomeTask(taskId) {
    return this.apiClient.post(`/api/outcome_task/${taskId}/complete`);
  }

  /**
   * Delete an outcome task
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Response data
   */
  async deleteOutcomeTask(taskId) {
    return this.apiClient.post(`/api/outcome_task/${taskId}/delete`);
  }

  /**
   * Add a comment to an outcome task
   * @param {string} taskId - Task ID
   * @param {string} commentText - Comment text
   * @returns {Promise<Object>} Response data
   */
  async addTaskComment(taskId, commentText) {
    return this.apiClient.post(`/api/outcome_task/${taskId}/comment/add`, {
      comment: commentText
    });
  }

  /**
   * Search for outcome tasks
   * @param {string} query 
   * @returns {Promise<Array>} Search results
   */
  async fetchOutcomeTask(taskId, options = {}) {
    const data = await this.apiClient.get(`/api/outcome_task/${taskId}`, options);
    return fromOutcomeTaskData(data);
  }

  async searchOutcomeTasks(query, options = {}) {
    const requestBody = { query: query };
    const data = await this.apiClient.post('/api/outcome_tasks/search', requestBody, options);
    return asOutcomeTaskList(data);
  }
}

